import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Compass, Package, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg, MODES, BASE } from "@/lib/api";
import { Section, StatusBadge, SampleBadge } from "@/components/Brand";
import { inputCls } from "@/components/Fields";
import { IcdPicker } from "@/components/IcdPicker";

export function ReviewQueue() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/review-queue").then((r) => setRows(r.data)); }, []);
  const groups = ["in_review", "approved", "draft"];
  return (
    <div className="space-y-5">
      <div><p className="eyebrow">Maker–checker</p><h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Review Queue</h1></div>
      {groups.map((g) => (
        <Section key={g} title={<span className="inline-flex items-center gap-2"><StatusBadge status={g} testid={`queue-badge-${g}`} />{rows.filter((r) => r.status === g).length}</span>} testid={`queue-${g}`}>
          <ul className="divide-y divide-slate-100">
            {rows.filter((r) => r.status === g).map((r) => (
              <li key={r.id} className="py-2 flex flex-wrap items-center gap-3 text-[13px]" data-testid={`queue-item-${r.id}`}>
                <Link to={`/protocols/${r.protocol_id}`} className="font-semibold text-slate-900 hover:text-sky-700">{r.protocol_title}</Link>
                <span className="font-mono text-[11px]">v{r.version}</span>
                <span className="text-slate-500">by {r.author_name}</span>
                {r.open_required > 0 && <span className="rounded bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">{r.open_required} required change(s)</span>}
                <span className="ml-auto text-[11px] text-slate-400">{r.updated_at?.slice(0, 16).replace("T", " ")}</span>
              </li>
            ))}
            {!rows.some((r) => r.status === g) && <li className="py-2 text-sm text-slate-500">Nothing here.</li>}
          </ul>
        </Section>
      ))}
    </div>
  );
}

export function Suggest() {
  const [f, setF] = useState({ icd10: "", condition: "", body_code: "", post_op_day: "", care_mode: "", age_band: "", comorbidity: "", market: "IN" });
  const [res, setRes] = useState(null);
  const run = async () => {
    try {
      const body = { ...f, post_op_day: f.post_op_day === "" ? null : Number(f.post_op_day), comorbidity_flags: f.comorbidity.split(",").map((s) => s.trim()).filter(Boolean) };
      Object.keys(body).forEach((k) => body[k] === "" && (body[k] = null));
      setRes((await api.post("/suggest", body)).data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const I = (k, ph) => <input data-testid={`suggest-${k}`} className={inputCls} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />;
  const [icd, setIcd] = useState([]);
  return (
    <div className="space-y-5">
      <div><p className="eyebrow">Assistive only · never auto-assigns</p><h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Protocol Suggestions</h1></div>
      <Section title="Structured, non-identifying inputs" testid="suggest-form" right={<Compass size={15} className="text-slate-400" />}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="sm:col-span-2"><IcdPicker testid="suggest-icd10" multiple={false} value={icd} onChange={(x) => { setIcd(x); setF({ ...f, icd10: x[0]?.code || "" }); }} placeholder="ICD-10 (WHO) – disease or code" /></div>
          {I("condition", "Condition keyword")}{I("body_code", "Body code (e.g. BJ-KNEE)")}{I("post_op_day", "Post-op day")}
          <select data-testid="suggest-care_mode" className={inputCls} value={f.care_mode} onChange={(e) => setF({ ...f, care_mode: e.target.value })}><option value="">Any care mode</option>{Object.entries(MODES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          {I("age_band", "Age band (e.g. ≥ 75)")}{I("comorbidity", "Comorbidity categories (comma)")}
          <select data-testid="suggest-market" className={inputCls} value={f.market} onChange={(e) => setF({ ...f, market: e.target.value })}><option value="IN">India</option><option value="MY">Malaysia</option></select>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">Do not enter names or other patient-identifiable details.</p>
        <button data-testid="suggest-run-btn" onClick={run} className="mt-3 rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Find candidates</button>
      </Section>
      {res && <div className="space-y-3" data-testid="suggest-results">
        <p className="rounded border border-sky-200 bg-sky-50 p-3 text-[13px] text-sky-900">{res.notice}</p>
        {res.candidates.map((c, i) => (
          <div key={c.protocol_id} className="card-surface p-4" data-testid={`candidate-${i}`}>
            <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-[12px] font-bold text-sky-700">#{i + 1} · {c.score}</span>
              <Link to={`/protocols/${c.protocol_id}`} className="font-heading font-bold">{c.title}</Link><span className="font-mono text-[11px]">v{c.version}</span>{c.is_sample && <SampleBadge />}</div>
            <ul className="mt-1 text-[13px] text-slate-700">{c.reasons.map((r, j) => <li key={j}>✓ {r}</li>)}</ul>
            {c.warnings.length > 0 && <ul className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-800">{c.warnings.map((w, j) => <li key={j}><AlertTriangle size={12} className="inline mr-1" />{w}</li>)}</ul>}
            <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-[12px] text-red-800"><b>Red flags:</b> {c.red_flags.map((r) => r.flag).join(" · ")}<br /><b>Contraindications:</b> {c.contraindications.join(" · ")}</div>
          </div>
        ))}
        {!res.candidates.length && <p className="text-sm text-slate-500">No candidates matched.</p>}
      </div>}
    </div>
  );
}

export function AuditLog() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ action: "", actor: "", entity_id: "" });
  const [open, setOpen] = useState(null);
  useEffect(() => { const t = setTimeout(() => api.get("/admin/audit", { params: f }).then((r) => setRows(r.data)), 200); return () => clearTimeout(t); }, [f]);
  return (
    <div className="space-y-5">
      <div><p className="eyebrow">Append-only · who, what, when, diff</p><h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Audit Trail</h1></div>
      <div className="grid sm:grid-cols-3 gap-2 max-w-3xl">
        {["action", "actor", "entity_id"].map((k) => <input key={k} data-testid={`audit-filter-${k}`} className={inputCls} placeholder={`Filter ${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />)}
      </div>
      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3" data-testid="audit-timeline">
        {rows.map((a) => (
          <li key={a.id} className="ml-4" data-testid={`audit-row-${a.id}`}>
            <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full border-2 border-white bg-sky-500" />
            <div className="text-[12px]"><span className="font-mono text-slate-400">{a.ts.slice(0, 19).replace("T", " ")}</span> · <b className="font-mono text-sky-800">{a.action}</b> · {a.actor_email || "system"} <span className="text-slate-400">({a.actor_role})</span> · <code className="text-slate-500">{a.entity}:{String(a.entity_id).slice(0, 8)}</code>
              {(a.diff?.length > 0 || Object.keys(a.meta || {}).length > 0) && <button className="ml-2 text-sky-700 font-semibold" onClick={() => setOpen(open === a.id ? null : a.id)} data-testid={`audit-toggle-${a.id}`}>{open === a.id ? "hide" : `details${a.diff?.length ? ` (${a.diff.length} changes)` : ""}`}</button>}</div>
            {open === a.id && <pre className="mt-1 max-h-72 overflow-auto rounded bg-slate-900 p-2 text-[11px] text-slate-100">{JSON.stringify({ diff: a.diff, meta: a.meta }, null, 2)}</pre>}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Pack() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [title, setTitle] = useState("Hospital Rehabilitation Programme Pack");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/protocols").then((r) => setRows(r.data.filter((p) => p.current_version))); }, []);
  const build = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pdf/pack", { version_ids: sel, title }, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "praniq-programme-pack.pdf"; a.click();
      toast.success("Programme pack generated");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <div><p className="eyebrow">Multi-protocol bundle</p><h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Hospital / In-plant Programme Pack</h1></div>
      <input data-testid="pack-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="card-surface divide-y divide-slate-100">
        {rows.map((p) => (
          <label key={p.id} className="flex items-center gap-3 p-3 text-[13px]"><input data-testid={`pack-select-${p.slug}`} type="checkbox" checked={sel.includes(p.current_version.id)} onChange={(e) => setSel(e.target.checked ? [...sel, p.current_version.id] : sel.filter((x) => x !== p.current_version.id))} />
            <b>{p.title}</b><span className="font-mono text-[11px]">v{p.current_version.version}</span><StatusBadge status={p.current_version.status} testid={`pack-status-${p.slug}`} /></label>
        ))}
      </div>
      <button data-testid="pack-build-btn" disabled={!sel.length || busy} onClick={build} className="inline-flex items-center gap-2 rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><Package size={15} />{busy ? "Generating…" : `Generate pack (${sel.length})`}</button>
    </div>
  );
}

export function Verify() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const docId = window.location.pathname.split("/").pop();
  useEffect(() => { fetch(`${BASE}/api/public/verify/${docId}`).then(async (r) => (r.ok ? setD(await r.json()) : setErr("Unknown document ID"))); }, [docId]);
  const ok = d?.status === "published" && d?.is_current;
  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
      <div className="card-surface w-full max-w-lg p-6 space-y-4" data-testid="verify-page">
        <div className="flex items-center gap-2"><svg width="30" height="30" viewBox="0 0 40 40"><polygon points="20,2 36,11 36,29 20,38 4,29 4,11" fill="none" stroke="#0284C7" strokeWidth="3" /><polyline points="8,21 14,21 17,13 22,28 25,21 32,21" fill="none" stroke="#0D9488" strokeWidth="2.6" /></svg><span className="font-heading font-extrabold text-sky-700">PranIQ · Document verification</span></div>
        {err && <p className="text-red-700" data-testid="verify-error">{err}</p>}
        {d && <>
          <div className={`flex items-center gap-3 rounded border p-4 ${ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="verify-result">
            {ok ? <CheckCircle2 className="text-emerald-600" /> : <XCircle className="text-red-600" />}
            <div><div className={`font-heading font-bold ${ok ? "text-emerald-800" : "text-red-800"}`}>{ok ? "Current published version" : d.status === "superseded" ? "SUPERSEDED — do not use" : d.status === "retired" ? "RETIRED — do not use" : `Not released (${d.status})`}</div>
              <div className="text-[13px] text-slate-600">{d.protocol_title} · v{d.version}</div></div>
          </div>
          {d.is_sample && <p className="rounded border border-red-300 bg-red-50 p-2 text-[12px] font-semibold text-red-700">SAMPLE – NOT CLINICALLY APPROVED</p>}
          <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-[13px]">
            <dt className="eyebrow">Document ID</dt><dd className="font-mono">{d.doc_id}</dd>
            <dt className="eyebrow">Current version</dt><dd>{d.current_version ? `v${d.current_version.version} (${d.current_version.doc_id})` : "—"}</dd>
            <dt className="eyebrow">Approved</dt><dd>{d.approved_at?.slice(0, 10) || "—"} · {d.approver_role || "—"}</dd>
            <dt className="eyebrow">Markets</dt><dd>{(d.approved_markets || []).join(", ")}</dd>
            <dt className="eyebrow">Review due</dt><dd>{d.review_due}</dd>
            <dt className="eyebrow">Checked</dt><dd className="font-mono text-[11px]">{d.checked_at}</dd>
          </dl>
        </>}
        <p className="text-[11px] text-slate-400">PranIQ Health Tech Private Limited · www.praniq.life. This protocol supports, and does not replace, the judgement of the treating clinician and the ordering doctor.</p>
      </div>
    </div>
  );
}
